/**
 * UNE FIGURE GÉOMÉTRIQUE ANNOTÉE DANS UNE FICHE — capacité `CAP-06` (tranche T4
 * du chantier « mathématiques de bout en bout »).
 *
 * Une fiche de géométrie montre un triangle coté, et le prof écrit DESSUS : la
 * formule d'un côté (`\sqrt{a^{2}+b^{2}}`), une étiquette (« hypoténuse » se
 * dit dans le texte ; `c^{2} = a^{2} + b^{2}` s'écrit sur la figure), une
 * flèche qui montre l'angle droit. La figure v2 sait dessiner le triangle, ses
 * noms, ses cotes en TEXTE (40 caractères, sans formule : le moteur les écrit
 * en SVG, à chasse fixe). Elle ne sait pas mettre une formule en page.
 *
 * ─── On étend, on ne duplique pas ───────────────────────────────────────────
 *
 * Rien n'est ajouté au moteur des figures, qui sert aussi les défis, l'atelier
 * et la copie imprimable : sa limite de 40 caractères sans LaTeX reste. Les
 * annotations sont une COUCHE de la fiche, posée par-dessus la liste
 * d'affichage du moteur, dans le MÊME repère d'affichage :
 *
 *  - la formule est mise en page par le moteur des formules (`disposerFormule`,
 *    celui de toute la fiche) ;
 *  - la flèche est celle du registre des dessins (`resoudreDessin`) ;
 *  - le tout sort en `TraceDessin`, que l'application et la console savent
 *    déjà peindre (`DessinVue`, `DessinWeb`).
 *
 * L'auteur écrit dans le REPÈRE DE LA FIGURE (y vers le haut), jamais en
 * pixels : un point se désigne par son nom (`"A"`) ou par ses coordonnées.
 *
 * ─── Ce que l'import refuse (`griefsDeFigureDeFiche`) ───────────────────────
 *
 *  1. une annotation sur un solide (la couche vit dans le plan) ;
 *  2. un point nommé que la figure ne porte pas, une formule hors liste ;
 *  3. RIEN NE SORT DE LA TOILE : un ancrage hors de la fenêtre du repère, une
 *     annotation qui déborde de la fenêtre de plus d'une marge ;
 *  4. AUCUN CHEVAUCHEMENT : deux écritures l'une sur l'autre (annotations, noms
 *     de points, cotes et étiquettes du moteur), une écriture d'annotation
 *     traversée par un trait de la figure.
 *
 * Le point 4, entre écritures du MOTEUR, vaut pour toute figure v2 de fiche,
 * annotée ou non : c'est le défaut qu'aucun schéma ne voit.
 */
import { MARGE_CADRE_REPERE_V2, TAILLE_POLICE_V2, boiteTexteV2, dessinerPlanAvecEtiquettes, normaliserFigureV2, } from '../figures/figure-v2.js';
import { dessinerEspace } from '../figures/vue3d.js';
import { disposerFormule, etendueDeTrace, resoudreDessin } from './dessin.js';
/** Le corps d'une formule d'annotation, en unités d'affichage : un cran au-dessus des noms. */
export const TAILLE_ANNOTATION_FIGURE = TAILLE_POLICE_V2 + 1;
/** L'écart entre un côté et la formule qui le cote. */
const ECART_COTE = 7;
/** La marge du cadre autour de tout ce qui est dessiné, comme le moteur. */
const MARGE_CADRE = 0.6 * TAILLE_POLICE_V2;
const versBoite = (v) => ({
    g: v.x,
    d: v.x + v.largeur,
    h: v.y,
    b: v.y + v.hauteur,
});
/** Deux boîtes se recouvrent — un effleurement (moins d'une demi-unité) ne compte pas. */
const seRecouvrent = (p, q) => p.g + 0.5 < q.d && q.g + 0.5 < p.d && p.h + 0.5 < q.b && q.h + 0.5 < p.b;
/** Le segment [a, z] traverse-t-il l'intérieur de la boîte (rétrécie d'une unité) ? */
function traverse(a, z, boite) {
    const b = { g: boite.g + 1, d: boite.d - 1, h: boite.h + 1, b: boite.b - 1 };
    if (b.g >= b.d || b.h >= b.b)
        return false;
    let t0 = 0;
    let t1 = 1;
    const dx = z.x - a.x;
    const dy = z.y - a.y;
    const coupes = [
        [-dx, a.x - b.g],
        [dx, b.d - a.x],
        [-dy, a.y - b.h],
        [dy, b.b - a.y],
    ];
    for (const [p, q] of coupes) {
        if (Math.abs(p) < 1e-12) {
            if (q < 0)
                return false;
        }
        else if (p < 0)
            t0 = Math.max(t0, q / p);
        else
            t1 = Math.min(t1, q / p);
        if (t0 > t1)
            return false;
    }
    return true;
}
/** Les traits d'une primitive du moteur, en segments — ce qu'une écriture ne doit pas chevaucher. */
function segmentsDe(p) {
    /* La grille et les axes passent SOUS les écritures : le moteur lui-même pose
       ses graduations dessus. */
    if ('calque' in p && (p.calque === 'grille' || p.calque === 'axe'))
        return [];
    const chaine = (pts, ferme) => {
        const s = [];
        for (let i = 0; i + 1 < pts.length; i += 1)
            s.push([pts[i], pts[i + 1]]);
        if (ferme && pts.length > 2)
            s.push([pts[pts.length - 1], pts[0]]);
        return s;
    };
    switch (p.type) {
        case 'trait':
            return [
                [
                    { x: p.x1, y: p.y1 },
                    { x: p.x2, y: p.y2 },
                ],
            ];
        case 'polyligne':
        case 'arc':
        case 'carre_droit':
            return chaine(p.points, false);
        case 'polygone':
            return chaine(p.points, true);
        case 'cercle': {
            const pts = Array.from({ length: 24 }, (_, k) => ({
                x: p.cx + p.r * Math.cos((k * Math.PI) / 12),
                y: p.cy + p.r * Math.sin((k * Math.PI) / 12),
            }));
            return chaine(pts, true);
        }
        /* Ni trait ni contour. Une SURFACE n'en a pas non plus : son contour, s'il est
           tracé, a ses propres primitives. */
        case 'marque':
        case 'texte':
        case 'surface':
            return [];
    }
}
const nomDe = (a) => a.type === 'fleche' ? 'la flèche' : `« ${a.tex} »`;
/** Une figure du plan et ses annotations : la couche, ses boîtes d'écriture, ses griefs. */
function poserLesAnnotations(figure, annotations) {
    const { liste, etiquettes } = dessinerPlanAvecEtiquettes(figure);
    const { kx, ky, xMin, yMax } = etiquettes.echelle;
    const r = figure.repere;
    const aff = (p) => ({ x: (p.x - xMin) * kx, y: (yMax - p.y) * ky });
    const fenetre = {
        g: -MARGE_CADRE_REPERE_V2,
        d: (r.xMax - xMin) * kx + MARGE_CADRE_REPERE_V2,
        h: -MARGE_CADRE_REPERE_V2,
        b: (yMax - r.yMin) * ky + MARGE_CADRE_REPERE_V2,
    };
    const noms = new Map();
    for (const o of figure.objets)
        if (o.type === 'point')
            noms.set(o.nom, { x: o.x, y: o.y });
    const traces = [];
    const ecritures = [];
    const griefs = [];
    annotations.forEach((a, i) => {
        const ou = `annotations[${i}]`;
        const point = (ref, cle) => {
            const p = typeof ref === 'string' ? noms.get(ref) : ref;
            if (p === undefined) {
                griefs.push(`${ou}.${cle} : la figure ne porte aucun point « ${String(ref)} »`);
                return null;
            }
            if (p.x < r.xMin || p.x > r.xMax || p.y < r.yMin || p.y > r.yMax) {
                griefs.push(`${ou}.${cle} : (${p.x} ; ${p.y}) est hors de la fenêtre du repère — rien ne sort de la toile`);
                return null;
            }
            return p;
        };
        /** Pose une formule dont l'ENCRE est centrée sur `c` (affichage). */
        const formule = (c, tex) => {
            const boite = disposerFormule(tex);
            if (boite === null) {
                griefs.push(`${ou}.tex : la formule « ${tex} » ne se dessine pas`);
                return;
            }
            const t = TAILLE_ANNOTATION_FIGURE;
            const trace = {
                type: 'formule',
                x: c.x - (boite.largeur * t) / 2,
                y: c.y + ((boite.montee - boite.descente) / 2) * t,
                taille: t,
                boite,
                ton: 'encre',
            };
            traces.push(trace);
            ecritures.push({ boite: etendueDeTrace(trace), quoi: `${ou} (« ${tex} »)` });
        };
        const debut = traces.length;
        switch (a.type) {
            case 'etiquette': {
                const p = point({ x: a.x, y: a.y }, 'x');
                if (p !== null)
                    formule(aff(p), a.tex);
                break;
            }
            case 'cote': {
                const de = point(a.de, 'de');
                const vers = point(a.a, 'a');
                if (de === null || vers === null)
                    break;
                const boite = disposerFormule(a.tex);
                if (boite === null) {
                    griefs.push(`${ou}.tex : la formule « ${a.tex} » ne se dessine pas`);
                    break;
                }
                const p = aff(de);
                const q = aff(vers);
                const dx = q.x - p.x;
                const dy = q.y - p.y;
                const longueur = Math.hypot(dx, dy);
                if (longueur < 1e-6) {
                    griefs.push(`${ou} : les deux bouts de la cote sont le même point`);
                    break;
                }
                /* À GAUCHE du sens `de → a`, tel qu'on le lit sur la figure (y vers le
                   haut) ; `cote: 'droite'` le retourne. En affichage, y descend. */
                const sens = a.cote === 'droite' ? -1 : 1;
                const n = { x: (sens * dy) / longueur, y: (-sens * dx) / longueur };
                const t = TAILLE_ANNOTATION_FIGURE;
                const demi = (Math.abs(n.x) * boite.largeur * t) / 2 +
                    (Math.abs(n.y) * (boite.montee + boite.descente) * t) / 2;
                formule({
                    x: (p.x + q.x) / 2 + n.x * (ECART_COTE + demi),
                    y: (p.y + q.y) / 2 + n.y * (ECART_COTE + demi),
                }, a.tex);
                break;
            }
            case 'fleche': {
                const de = point(a.de, 'de');
                const vers = point(a.a, 'a');
                const par = a.par === undefined ? undefined : point(a.par, 'par');
                if (de === null || vers === null || par === null)
                    break;
                const p = aff(de);
                const q = aff(vers);
                if (Math.hypot(q.x - p.x, q.y - p.y) < 1e-6) {
                    griefs.push(`${ou} : la flèche part de là où elle arrive`);
                    break;
                }
                const m = par === undefined ? { x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 } : aff(par);
                const resolu = resoudreDessin({
                    genre: 'dessin',
                    decrit: '',
                    hauteur: 1,
                    elements: [
                        { type: 'fleche', de: [p.x, p.y], par: [m.x, m.y], a: [q.x, q.y], ton: 'doux' },
                    ],
                });
                traces.push(...resolu.traces);
                break;
            }
        }
        /* 3. Rien ne sort de la toile. */
        for (const t of traces.slice(debut)) {
            const e = etendueDeTrace(t);
            if (e.g < fenetre.g || e.d > fenetre.d || e.h < fenetre.h || e.b > fenetre.b) {
                griefs.push(`${ou} : ${nomDe(a)} sort de la toile — rapprocher l’annotation de la figure`);
                break;
            }
        }
    });
    return { liste, traces, ecritures, griefs };
}
/** Les écritures du MOTEUR (noms, cotes, étiquettes, graduations) : leurs boîtes. */
function ecrituresDuMoteur(liste) {
    return liste.primitives.flatMap((p) => p.type === 'texte'
        ? [{ boite: versBoite(boiteTexteV2(p)), quoi: `« ${p.texte} »`, role: p.role }]
        : []);
}
/** Le `viewBox` qui englobe la liste du moteur ET la couche des annotations. */
function cadreCommun(liste, traces) {
    if (traces.length === 0)
        return liste;
    const v = versBoite(liste.viewBox);
    let { g, d, h, b } = v;
    for (const t of traces) {
        const e = etendueDeTrace(t);
        g = Math.min(g, e.g - MARGE_CADRE);
        d = Math.max(d, e.d + MARGE_CADRE);
        h = Math.min(h, e.h - MARGE_CADRE);
        b = Math.max(b, e.b + MARGE_CADRE);
    }
    if (g === v.g && d === v.d && h === v.h && b === v.b)
        return liste;
    const viewBox = { x: g, y: h, largeur: d - g, hauteur: b - h };
    return { ...liste, viewBox, rapport: viewBox.largeur / viewBox.hauteur };
}
function dessiner(figure) {
    return figure.espace === 'espace'
        ? dessinerEspace(figure)
        : dessinerPlanAvecEtiquettes(figure).liste;
}
/**
 * Résout la figure v2 d'une fiche : la liste d'affichage du moteur, et la
 * couche de ses annotations. Ne lève jamais. `null` : le moteur refuse la
 * figure elle-même (`normaliserFigureV2`) — l'appelant montre alors son
 * repli habituel.
 *
 * Sans annotation, la liste est CELLE du moteur, à l'identique : une fiche
 * d'avant se dessine comme avant.
 */
export function resoudreFigureDeFiche(illustration) {
    const n = normaliserFigureV2(illustration.figure);
    if ('griefs' in n)
        return null;
    const annotations = illustration.annotations ?? [];
    if (annotations.length === 0)
        return { liste: dessiner(n.figure), traces: [], griefs: [] };
    if (n.figure.espace !== 'plan') {
        return {
            liste: dessiner(n.figure),
            traces: [],
            griefs: [
                'annotations : une figure dans l’espace ne s’annote pas (la couche vit dans le plan)',
            ],
        };
    }
    const pose = poserLesAnnotations(n.figure, annotations);
    return {
        liste: cadreCommun(pose.liste, pose.traces),
        traces: pose.traces,
        griefs: pose.griefs,
    };
}
/**
 * Ce que l'import refuse d'une figure v2 de fiche (voir l'en-tête). Les griefs
 * du moteur d'abord — une figure qu'il refuse n'est pas examinée plus loin.
 */
export function griefsDeFigureDeFiche(illustration) {
    const n = normaliserFigureV2(illustration.figure);
    if ('griefs' in n)
        return n.griefs;
    const annotations = illustration.annotations ?? [];
    if (n.figure.espace !== 'plan') {
        return annotations.length === 0
            ? []
            : ['annotations : une figure dans l’espace ne s’annote pas (la couche vit dans le plan)'];
    }
    const pose = poserLesAnnotations(n.figure, annotations);
    const griefs = [...pose.griefs];
    const moteur = ecrituresDuMoteur(pose.liste);
    /* 4a. Deux écritures du moteur l'une sur l'autre. Les graduations d'un axe
       sont posées par le moteur sur une grille régulière : hors d'examen. */
    const lisibles = moteur.filter((e) => e.role !== 'graduation');
    for (let i = 0; i < lisibles.length; i += 1) {
        for (let j = i + 1; j < lisibles.length; j += 1) {
            if (seRecouvrent(lisibles[i].boite, lisibles[j].boite)) {
                griefs.push(`figure : ${lisibles[i].quoi} et ${lisibles[j].quoi} se chevauchent — écarter les points, ou raccourcir un texte`);
            }
        }
    }
    /* 4b. Une écriture d'annotation sur une autre écriture, ou sous un trait. */
    const segments = pose.liste.primitives.flatMap(segmentsDe);
    pose.ecritures.forEach((e, i) => {
        for (const autre of pose.ecritures.slice(i + 1)) {
            if (seRecouvrent(e.boite, autre.boite)) {
                griefs.push(`${e.quoi} et ${autre.quoi} se chevauchent`);
            }
        }
        for (const m of moteur) {
            if (seRecouvrent(e.boite, m.boite)) {
                griefs.push(`${e.quoi} chevauche ${m.quoi}, que la figure écrit déjà`);
            }
        }
        if (segments.some(([a, z]) => traverse(a, z, e.boite))) {
            griefs.push(`${e.quoi} est traversée par un trait de la figure — la décaler`);
        }
    });
    return griefs;
}
//# sourceMappingURL=figure-annotee.js.map